import { createCollectionLink } from "@/router/linkFactories";
import { RouteName } from "@/router/routeNameEnum";
import { routerRef, whenRouterReady } from "@/router/routerRef";
import PostHog from "@/services/analytics/postHog";
import { consumeCheckoutAuthIntent } from "@/services/user/auth/login/checkoutAuthIntent";
import { clearAfterAuthRedirect, getAfterAuthRedirect } from "@/services/afterAuthRedirectService";
import { CartStatus, type ShoppingCart } from "@/services/marketplace/cart/cartTypes";
import { useAnonymousIdentifier } from "@/composables/anonymousIdentifier.client";
import { createAddress } from "@/services/addresses/addressApi";
import type { UserId } from "@/services/user/userTypes";
import type { RouteLocationAsPathGeneric, RouteLocationAsRelativeGeneric, Router } from "vue-router";

type Redirect = string | RouteLocationAsPathGeneric | RouteLocationAsRelativeGeneric | undefined;

export const afterLogin = async (): Promise<void> => {
  await mergeAnonymousCartIfExists(); // Must be done before redirect, otherwise cart will be empty
  trackCheckoutAuthCompleted();
  await redirectAfterLogin();
};

const trackCheckoutAuthCompleted = (): void => {
  if (consumeCheckoutAuthIntent()) {
    PostHog.trackEvent(PostHog.EventName.Checkout_AuthCompleted);
  }
};

const mergeAnonymousCartIfExists = async (): Promise<void> => {
  // Lazy imports break circular dependencies: this file is reachable from axiosFactory.ts's
  // login handling (cart module chain → ApiClient/paymentMethodSchema).
  const { fetchCartStatus, fetchFullCart, mergeAnonymousCart } = await import("@/services/marketplace/cart/cartApi");
  const { useCart } = await import("@/composables/marketplace/cart/cart");
  const { useAnonymousCartPreferences } = await import("@/composables/marketplace/cart/useAnonymousCartPreferences.client");
  const { useCartStatus } = await import("@/composables/marketplace/cart/useCartStatus");

  const anonymousIdentifier = useAnonymousIdentifier().getIdentifier();
  const status = await fetchCartStatus(anonymousIdentifier);

  const currentRoute = routerRef?.currentRoute.value;
  const sellerNickname = currentRoute?.name === RouteName.MarketplaceOrder ? (currentRoute.params.sellerNickname as string) : undefined;

  if (status === CartStatus.NotEmpty) {
    const mergedCart = await mergeAnonymousCart(anonymousIdentifier);
    const sellerId = sellerNickname !== undefined ? mergedCart?.subcarts.find(({ sellerNickname: nick }) => nick === sellerNickname)?.sellerId : undefined;
    await applyLocalPreferencesToMergedCart(mergedCart, sellerId);
  }
  useAnonymousCartPreferences().clearPreferences();
  useCartStatus().refresh();

  if (sellerNickname !== undefined) {
    const { cart } = useCart();
    const sellerId = cart.value?.subcarts.find(({ sellerNickname: nick }) => nick === sellerNickname)?.sellerId;
    if (sellerId !== undefined) {
      cart.value = await fetchFullCart(sellerId);
    }
  }
};

const applyLocalPreferencesToMergedCart = async (mergedCart: ShoppingCart | undefined, selectedSellerId?: UserId): Promise<void> => {
  const { saveShippingMethod, savePaymentMethod, attachShippingAddressToCart } = await import("@/services/marketplace/cart/cartApi");
  const { useAnonymousCartPreferences } = await import("@/composables/marketplace/cart/useAnonymousCartPreferences.client");
  const { selectedShippingMethod, selectedPaymentMethod, zasilkovnaAddress } = useAnonymousCartPreferences();

  const applyAddress = async (): Promise<void> => {
    if (zasilkovnaAddress.value === null || mergedCart?.shippingAddress !== null) {
      return;
    }
    const address = await createAddress(zasilkovnaAddress.value);
    if (mergedCart !== undefined) {
      await attachShippingAddressToCart(mergedCart, address.id, selectedSellerId);
    }
  };

  const saveShipping = selectedShippingMethod.value !== null && mergedCart?.selectedShippingMethod === null ? saveShippingMethod(selectedShippingMethod.value.id, selectedSellerId) : Promise.resolve();

  const savePayment = selectedPaymentMethod.value !== null && mergedCart?.selectedPaymentMethod === null ? savePaymentMethod(selectedPaymentMethod.value.id, selectedSellerId) : Promise.resolve();

  await Promise.all([saveShipping, savePayment, applyAddress()]);
};

const redirectAfterLogin = async (): Promise<Redirect> => {
  // Unset routerRef ⇒ we're inside the initial navigation, before App.vue mounted (cold start with
  // a stored token, e.g. the full page reload an SSO round trip ends with).
  const router = routerRef;
  let redirect;

  const pendingRedirect = await getAfterAuthRedirect();
  if (pendingRedirect !== undefined) {
    clearAfterAuthRedirect();
    redirect = pendingRedirect;
  } else if (router === undefined) {
    return undefined; // First load, and nowhere stored to go: the navigation already has its target
  } else {
    const route = router.currentRoute.value;
    const queryRedirect = route.query.redirect_url;
    const isValidRedirect = typeof queryRedirect === "string";
    if (route.name === undefined) {
      return undefined; // First load
    } else if (isValidRedirect) {
      redirect = queryRedirect;
    } else if (route.meta.redirectWhenLoggedIn) {
      redirect = route.meta.redirectWhenLoggedIn;
    } else if (route.meta.public) {
      return undefined;
    } else {
      redirect = createCollectionLink();
    }
  }

  if (redirect !== undefined) {
    navigate(router, redirect);
  }
};

/**
 * Chained, never awaited: the router only appears once App.vue mounts, which the navigation this
 * runs inside has to finish first. replace() rather than push() for the deferred case, so the URL
 * the cold start landed on doesn't become a history entry to go back to.
 */
const navigate = (router: Router | undefined, redirect: Exclude<Redirect, undefined>): void => {
  if (router !== undefined) {
    router.push(redirect);
  } else {
    void whenRouterReady().then((readyRouter) => readyRouter.replace(redirect));
  }
};
